import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getDebtorAddressInfo } from 'us.collection.debtor/actions';
import { useTranslation } from 'react-i18next';
import Common from 'us.common';
import { debtor } from 'us.collection.debtor/actions';
import { IMetaData } from 'us.collection/interfaces';
import { IDataModel } from 'us.collection.case/reducers/Dashboard/Interfaces';
import { IOnFilter, IOnSort } from 'us.common/components';
import { RootState } from 'us.helper/types';

const { $Skeleton, $Tag, $TableTree, $DateLabel } = Common.Components;

interface IAddressHistory {
	debtorAddressInfomation: any;
	getDebtorAddressInfo: () => void;
	onClose(): void;
	isFetching: boolean;
	currentCurrency: string;
	currentLanguage: string;
	getDebtorAddress: any;
	debtorAddressInfo: any;
	isFetchingAddressInfo: boolean;
	metaData: IDataModel<IMetaData>;
	isCreditor?: boolean;
	creditorMetaData: any;
}

const AddressHistory: React.FC<IAddressHistory> = (props) => {
	const { t } = useTranslation();
	const {
		getDebtorAddress,
		debtorAddressInfo,
		isFetchingAddressInfo,
		metaData,
		isCreditor,
		creditorMetaData,
	} = props;

	const { debtorId } = metaData.data ?? {};

	useEffect(() => {
		if (isCreditor) {
			if (creditorMetaData?.entRoleId) {
				getDebtorAddress(creditorMetaData?.entRoleId);
			}
		} else {
			if (debtorId) {
				getDebtorAddress(debtorId);
			}
		}
	}, [metaData]);

	const handleTableChange = (
		pagination: any,
		filters: any,
		sorter: any
	) => {};

	const columns: any = [
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.ROWSTATE'
			),
			dataIndex: 'rowState',
			key: 'rowState',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any, record: any) => {
				return record.rowState === 'Active' ? (
					<$Tag
						className='tag-status-active'
						style={{
							minWidth: '60px',
							textAlign: 'center',
						}}>
						{text}
					</$Tag>
				) : (
					<$Tag
						className='tag-status-deactive'
						style={{
							minWidth: '60px',
							textAlign: 'center',
						}}>
						{text}
					</$Tag>
				);
			},
		},
		{
			title: t('US.COLLECTION.DEBTOR:ADDRESSHISTORY.SOURCE'),
			dataIndex: 'addrsource',
			key: 'addrsource',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.ADDRESS1'
			),
			dataIndex: 'address1',
			key: 'address1',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.ADDRESS2'
			),
			dataIndex: 'address2',
			key: 'address2',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.ADDRESS3'
			),
			dataIndex: 'address3',
			key: 'address3',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.POSTAL_AREA'
			),
			dataIndex: 'zipName',
			key: 'zipName',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.MUNICIPALITY'
			),
			dataIndex: 'municipality',
			key: 'municipality',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t('US.COLLECTION.DEBTOR:ADDRESSHISTORY.COUNTRY'),
			dataIndex: 'countryName',
			key: 'countryName',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.MODIFIED_DATE'
			),
			dataIndex: 'modifiedDate',
			key: 'modifiedDate',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: 'date',
			customRenderChild: (text: any) => {
				return (
					<span>
						<$DateLabel value={text} />
					</span>
				);
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.MODIFIEDUSER'
			),
			dataIndex: 'modifiedUser',
			key: 'modifiedUser',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t(
				'US.COLLECTION.DEBTOR:ADDRESSHISTORY.TELEPHONE'
			),
			dataIndex: 'telHome',
			key: 'telHome',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t('US.COLLECTION.DEBTOR:ADDRESSHISTORY.SMS'),
			dataIndex: 'sms',
			key: 'sms',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
		{
			title: t('US.COLLECTION.DEBTOR:ADDRESSHISTORY.EMAIL'),
			dataIndex: 'email',
			key: 'email',
			className: 'text-nowrap',
			customSorter: (a: any, b: any) => a.localeCompare(b),
			customFilter: true,
			customRenderChild: (text: any) => {
				return <span>{text}</span>;
			},
		},
	];

	const handleSort: IOnSort = (sortData, dataSource) => {
		return sortData(dataSource);
	};

	const handleFilter: IOnFilter = (searchData, dataSource) => {
		return searchData(dataSource);
	};

	return (
		<$Skeleton
			loading={isFetchingAddressInfo}
			active
			paragraph={{ rows: 2 }}>
			<$TableTree
				onChange={handleTableChange}
				rowClassName={(record: any) =>
					record.rowState === 'Active'
						? 'this-case'
						: ''
				}
				rowKey='id'
				data={debtorAddressInfo}
				size='small'
				className=''
				onSort={handleSort}
				onFilter={handleFilter}
				filterOnType={true}
				resetOnSourceChange={true}
				bordered
				pagination={{ defaultPageSize: 15 }}
				scroll={{ x: 1200 }}
				columns={columns}
			/>
		</$Skeleton>
	);
};

const mapStateToProps = (state: RootState) => {
	const { common, debtor, entity, debtorDetail, domainView, creditor } =
		state;
	const { address } = debtorDetail;
	const { currentCurrency, currentLanguage } = common;
	const { metaData } = domainView;
	const { general } = creditor;
	const { data } = general;
	return {
		debtorAddressInfomation: debtor.debtorAddressInfo,
		currentCurrency,
		currentLanguage,
		isFetching: entity.isFetching,
		debtorAddressInfo: address.data,
		isFetchingAddressInfo: address.isFetching,
		metaData,
		creditorMetaData: data,
	};
};

const mapDispatchToProps = {
	getDebtorAddressInfo,
	getDebtorAddress: debtor.address.getByDebtorNo,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddressHistory);
